/**
 * Canonical registry identifiers, and the conversion between the human-readable
 * name and the `bytes32` the chain stores.
 *
 * One place computes `keccak256("ASETQU_*")` for the whole portal. A second
 * place computing it independently is a second chance to typo an identifier
 * that nothing on-chain will ever reject — the registry cannot tell a mistyped
 * id from an id that simply does not exist yet.
 */
import { keccak256, toHex } from "viem";

/** Ids the ecosystem publishes. Not exhaustive: vendors get generated ids. */
export const KNOWN_REGISTRY_NAMES = [
  "ASETQU_MEMBERSHIP",
  "ASETQU_ACADEMY",
  "ASETQU_VOUCHER",
  "ASETQU_AUCTION",
  "ASETQU_MARKETPLACE",
  "ASETQU_E_LEARNING",
  "ASETQU_PROPERTY_CLUB",
  "ASETQU_SUBSCRIPTION",
] as const;

export type KnownRegistryName = (typeof KNOWN_REGISTRY_NAMES)[number];

const NAME_PATTERN = /^ASETQU_[A-Z0-9_]+$/;
const HASH_PATTERN = /^0x[0-9a-fA-F]{64}$/;

export function isRegistryName(value: string): boolean {
  return NAME_PATTERN.test(value);
}

export function isRegistryHash(value: string): boolean {
  return HASH_PATTERN.test(value);
}

/** `ASETQU_MEMBERSHIP` → `keccak256(bytes("ASETQU_MEMBERSHIP"))`. */
export function registryIdOf(name: string): `0x${string}` {
  if (!isRegistryName(name)) {
    throw new Error(`Not a registry name: ${name}`);
  }
  return keccak256(toHex(name));
}

/**
 * Accepts either form from a URL and returns the `bytes32`.
 *
 * Names are preferred in links because they are readable and checkable by eye.
 * Raw hashes are accepted so a link copied out of BscScan still resolves.
 */
export function resolveRegistryId(input: string): `0x${string}` | null {
  const value = decodeURIComponent(input).trim();
  if (isRegistryHash(value)) return value.toLowerCase() as `0x${string}`;
  if (isRegistryName(value)) return registryIdOf(value);
  return null;
}

/**
 * Best-effort reverse lookup, for display only.
 *
 * keccak256 is one-way, so this can only recognise ids it was told about. An
 * unknown id is not suspicious — every vendor id is generated and will land
 * here. Never gate anything on this returning null.
 */
export function nameForRegistryId(id: string): string | null {
  const target = id.toLowerCase();
  for (const name of KNOWN_REGISTRY_NAMES) {
    if (registryIdOf(name).toLowerCase() === target) return name;
  }
  return null;
}

/**
 * Whether `name` is the preimage of `id`.
 *
 * This is what makes it safe to display a readable label that came from an
 * untrusted source. `nameForRegistryId` can only recognise the eight names
 * compiled in, so every generated vendor id renders as a hash — but a claimed
 * name is not a claim at all once it is checked: only the real preimage hashes
 * to the id, and nobody can produce a second one.
 *
 * So a supplied name is either provably correct or discarded. There is no
 * middle case where the certificate prints something plausible-but-unverified,
 * which is what a "trusted label" field would have been.
 */
export function matchesRegistryId(name: string, id: string): boolean {
  if (!isRegistryName(name)) return false;
  return registryIdOf(name).toLowerCase() === id.toLowerCase();
}
